/**
 * SortableJS helpers — the native replacement for the old `$.fn.sortable` shim.
 *
 * The jQuery plugin kept its instance in `$el.data("sortable")` so callers could
 * re-init a list by destroying the previous instance first. That registry lives
 * here instead, keyed on the element itself.
 */
import Sortable from "sortablejs";

const instances = new WeakMap<HTMLElement, Sortable>();

/**
 * Create a Sortable on `el`, replacing any instance already attached to it.
 * Re-initializing is the common case: callers rebuild a list's innerHTML and
 * then re-sort it.
 */
export function makeSortable(
  el: HTMLElement | string,
  options: Sortable.Options = {}
): Sortable | null {
  const element = typeof el === "string" ? document.getElementById(el) : el;
  if (!element) return null;

  destroySortable(element);
  const instance = new Sortable(element, options);
  instances.set(element, instance);
  return instance;
}

/** Destroy the Sortable attached to `el`, if any. No-op otherwise. */
export function destroySortable(el: HTMLElement | string): void {
  const element = typeof el === "string" ? document.getElementById(el) : el;
  if (!element) return;

  const existing = instances.get(element);
  if (existing) {
    existing.destroy();
    instances.delete(element);
  }
}
